/**
 * @author: Claude
 * @date: 2026/03/13
 * @desc: 左侧组件目录树组件
 */
import React, { useMemo, useState, useEffect } from 'react';
import { SearchBox, Icon } from 'tea-component';

import type { IComponentTreeProps, ITreeNode } from './types';

import './index.scss';

/**
 * 组件目录树
 */
const ComponentTree: React.FC<IComponentTreeProps> = ({ treeData, selectedKey, onSelect }) => {
  const [searchValue, setSearchValue] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  // 过滤树数据
  const filteredTreeData = useMemo(() => {
    if (!searchValue) return treeData;
    return filterTree(treeData, searchValue.toLowerCase());
  }, [treeData, searchValue]);

  // 外部通过 url 或刷新回显选中项时，自动展开其父级目录
  useEffect(() => {
    if (!selectedKey) {
      return;
    }

    const ancestorKeys = findAncestorKeys(treeData, selectedKey);
    if (!ancestorKeys) {
      return;
    }

    setExpandedKeys(new Set(ancestorKeys));
  }, [selectedKey, treeData]);

  // 切换展开状态（手风琴模式：同级只能展开一个）
  const toggleExpand = (key: string, siblingKeys: string[]) => {
    setExpandedKeys((prevExpanded) => {
      const newExpanded = new Set<string>();

      // 如果当前节点已展开，则折叠
      if (prevExpanded.has(key)) {
        // 保留其他层级的展开状态
        prevExpanded.forEach((k) => {
          if (!siblingKeys.includes(k)) {
            newExpanded.add(k);
          }
        });
      } else {
        // 展开当前节点，关闭其他同级节点
        prevExpanded.forEach((k) => {
          if (!siblingKeys.includes(k)) {
            newExpanded.add(k);
          }
        });
        newExpanded.add(key);
      }

      return newExpanded;
    });
  };

  // 处理选中
  const handleSelect = (node: ITreeNode, siblingKeys: string[]) => {
    if (node.isLeaf) {
      // 叶子节点：选中组件
      onSelect(node.key, node.componentInfo);
    } else {
      // 非叶子节点：展开/折叠
      toggleExpand(node.key, siblingKeys);
    }
  };

  // 渲染树节点
  const renderTreeNode = (node: ITreeNode, level = 0, siblingKeys: string[] = []): React.ReactNode => {
    const isExpanded = expandedKeys.has(node.key);
    const isSelected = selectedKey === node.key;
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div key={node.key} className="dbi-component-tree__node-group">
        <div
          className={`dbi-component-tree__node ${isSelected ? 'dbi-component-tree__node--selected' : ''}`}
          onClick={() => handleSelect(node, siblingKeys)}
        >
          {/* 展开/折叠图标 */}
          <span
            className={`dbi-component-tree__expand-icon ${
              isExpanded ? 'dbi-component-tree__expand-icon--expanded' : ''
            } ${!hasChildren ? 'dbi-component-tree__expand-icon--hidden' : ''}`}
            style={{ marginLeft: `${level * 16}px` }}
          >
            <Icon type="caret-right" />
          </span>
          {/* 标题 */}
          <span className="dbi-component-tree__node-title">{node.title}</span>
          {hasChildren && (
            <span
              className={`dbi-component-tree__submenu-icon ${
                isExpanded ? 'dbi-component-tree__submenu-icon--expanded' : ''
              }`}
            >
              <Icon type="arrowright" />
            </span>
          )}
        </div>
        {/* 子节点 */}
        {hasChildren && isExpanded && (
          <div className="dbi-component-tree__children">
            {node.children!.map((child) =>
              renderTreeNode(
                child,
                level + 1,
                node.children!.map((c) => c.key),
              ),
            )}
          </div>
        )}
      </div>
    );
  };

  // 获取根节点的 keys
  const rootSiblingKeys = useMemo(() => {
    return filteredTreeData.map((node) => node.key);
  }, [filteredTreeData]);

  return (
    <div className="dbi-component-tree">
      {/* 搜索框 */}
      <div className="dbi-component-tree__search">
        <SearchBox placeholder="搜索组件..." value={searchValue} onChange={setSearchValue} />
      </div>

      {/* 树结构 */}
      <div className="dbi-component-tree__content">
        {filteredTreeData.length > 0 ? (
          filteredTreeData.map((node) => renderTreeNode(node, 0, rootSiblingKeys))
        ) : (
          <div className="dbi-component-tree__empty">{searchValue ? '未找到匹配的组件' : '暂无组件'}</div>
        )}
      </div>
    </div>
  );
};

/**
 * 过滤树数据
 */
function filterTree(nodes: ITreeNode[], keyword: string): ITreeNode[] {
  const result: ITreeNode[] = [];

  for (const node of nodes) {
    const titleMatch = node.title.toLowerCase().includes(keyword);
    const childrenMatch = node.children && filterTree(node.children, keyword).length > 0;

    if (titleMatch) {
      result.push(node);
    } else if (childrenMatch) {
      result.push({
        ...node,
        children: filterTree(node.children!, keyword),
      });
    }
  }

  return result;
}

/**
 * 查找节点的所有祖先 key
 */
function findAncestorKeys(nodes: ITreeNode[], targetKey: string, ancestors: string[] = []): string[] | null {
  for (const node of nodes) {
    if (node.key === targetKey) {
      return ancestors;
    }

    if (node.children && node.children.length > 0) {
      const found = findAncestorKeys(node.children, targetKey, [...ancestors, node.key]);
      if (found) {
        return found;
      }
    }
  }

  return null;
}

export default ComponentTree;
